import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { usePagedData } from '../../hooks/usePagedData';
import { shipmentsApi } from '../../api/endpoints';
import { ShipmentList, ShipmentStatusMap } from '../../types';
import Select from '../../components/common/Select';
import { FiSearch, FiPlus, FiX, FiSend, FiDownload } from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import { toast } from 'react-toastify';

const ShipmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState({
    purchaseOrderId: '',
    carrierName: '',
    trackingNumber: '',
    shipDate: '',
  });
  const [creating, setCreating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<ShipmentList>(shipmentsApi.list, {
    initialSortBy: 'shipDate',
    initialSortDesc: true,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleRowClick = useCallback(
    (row: ShipmentList) => {
      navigate(`/shipments/${row.id}`);
    },
    [navigate]
  );

  const handleCreateSubmit = useCallback(async () => {
    setCreating(true);
    try {
      await shipmentsApi.create(createData);
      toast.success('Wysyłka utworzona');
      setShowCreateModal(false);
      setCreateData({
        purchaseOrderId: '',
        carrierName: '',
        trackingNumber: '',
        shipDate: '',
      });
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd tworzenia wysyłki');
    } finally {
      setCreating(false);
    }
  }, [createData, refresh]);

  const handleBulkSubmit = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    setBulkProgress({ current: 0, total: selectedIds.size });
    try {
      const result = await shipmentsApi.bulkSubmit(Array.from(selectedIds));
      const r = result.data;
      setBulkProgress({ current: r.total, total: r.total });
      toast.success(`Wysłano ${r.success} z ${r.total} wysyłek do Amazon`);
      if (r.success < r.total) {
        const errors = r.results.filter((x: any) => x.status === 'error');
        errors.forEach((e: any) => toast.error(`Wysyłka ${e.id.substring(0, 8)}: ${e.message}`));
      }
      setSelectedIds(new Set());
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd bulk wysyłki');
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedIds, refresh]);

  const handleExportCsv = useCallback(async () => {
    try {
      const response = await shipmentsApi.exportCsv(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipments_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV wyeksportowany');
    } catch (error: any) {
      toast.error('Błąd eksportu CSV');
    }
  }, [filters]);

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  const columns = [
    {
      key: 'purchaseOrderNumber',
      header: 'Numer PO',
      sortable: true,
    },
    {
      key: 'amazonShipmentId',
      header: 'ID wysyłki Amazon',
      render: (row: ShipmentList) => row.amazonShipmentId || '-',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: ShipmentList) => (
        <StatusBadge type="shipment" value={row.status} />
      ),
    },
    {
      key: 'shipDate',
      header: 'Data wysyłki',
      sortable: true,
      render: (row: ShipmentList) => formatDate(row.shipDate),
    },
    {
      key: 'carrierName',
      header: 'Przewoźnik',
      render: (row: ShipmentList) => row.carrierName || '-',
    },
    {
      key: 'trackingNumber',
      header: 'Numer śledzenia',
      render: (row: ShipmentList) => row.trackingNumber || '-',
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Wysyłki</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={handleExportCsv}>
            <FiDownload /> CSV
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.shipmentsXlsx, `wysylki_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus /> Nowa wysyłka
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span style={{ fontWeight: 600 }}>
            Zaznaczono: {selectedIds.size} {selectedIds.size === 1 ? 'wysyłka' : 'wysyłek'}
          </span>
          {bulkProcessing && (
            <div style={{ flex: 1, maxWidth: '200px' }}>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%` }}
                />
              </div>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                {bulkProgress.current}/{bulkProgress.total}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleBulkSubmit}
              disabled={bulkProcessing}
            >
              <FiSend /> {bulkProcessing ? 'Wysyłam...' : 'Wyślij zaznaczone do Amazon'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkProcessing}
            >
              <FiX /> Odznacz
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Szukaj wysyłek..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: 'Wszystkie statusy' },
              ...Object.entries(ShipmentStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder="Wszystkie statusy"
          />
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={7} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}

      {/* Create Shipment Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nowa wysyłka</h2>
              <button
                className="btn btn-icon"
                onClick={() => setShowCreateModal(false)}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>ID zamówienia zakupu</label>
                <input
                  type="text"
                  className="form-control"
                  value={createData.purchaseOrderId}
                  onChange={(e) =>
                    setCreateData((prev) => ({
                      ...prev,
                      purchaseOrderId: e.target.value,
                    }))
                  }
                  placeholder="ID zamówienia..."
                />
              </div>
              <div className="form-group">
                <label>Przewoźnik</label>
                <input
                  type="text"
                  className="form-control"
                  value={createData.carrierName}
                  onChange={(e) =>
                    setCreateData((prev) => ({
                      ...prev,
                      carrierName: e.target.value,
                    }))
                  }
                  placeholder="Nazwa przewoźnika..."
                />
              </div>
              <div className="form-group">
                <label>Numer śledzenia</label>
                <input
                  type="text"
                  className="form-control"
                  value={createData.trackingNumber}
                  onChange={(e) =>
                    setCreateData((prev) => ({
                      ...prev,
                      trackingNumber: e.target.value,
                    }))
                  }
                  placeholder="Numer śledzenia..."
                />
              </div>
              <div className="form-group">
                <label>Data wysyłki</label>
                <input
                  type="date"
                  className="form-control"
                  value={createData.shipDate}
                  onChange={(e) =>
                    setCreateData((prev) => ({
                      ...prev,
                      shipDate: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Anuluj
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateSubmit}
                disabled={creating || !createData.purchaseOrderId}
              >
                {creating ? 'Tworzenie...' : 'Utwórz wysyłkę'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentsPage;
